import { FilterKey, IFilterPayload } from '@studyportals/search-filters';
import { FilterSelectionStateReactive } from './FilterSelectionStateReactive';

export class FilterSelectionState {
	public reactive: FilterSelectionStateReactive;

	public constructor() {
		this.reactive = new FilterSelectionStateReactive();
	}

	public identifySelectedFiltersFor(state: FilterSelectionState): readonly FilterKey[] {
		return Array.from(state.reactive.identifyAllPossibleFilterKeys()).filter(
			(key) => state.reactive.getFor(key).length > 0
		);
	}

	public addValuesFor(key: FilterKey, values: readonly string[]): FilterSelectionState {
		const partial = this.copy();
		partial.reactive.set(key, [...partial.reactive.getFor(key), ...values]);
		return partial;
	}

	public addValueFor(key: FilterKey, value: string): FilterSelectionState {
		const partial = this.copy();
		partial.reactive.set(key, [...partial.reactive.getFor(key), value]);
		return partial;
	}

	public replace(key: FilterKey, values: readonly string[]): FilterSelectionState {
		const partial = this.copy();
		partial.reactive.set(key, [...values]);
		return partial;
	}

	public clearFor(keys: readonly FilterKey[], state: FilterSelectionState): FilterSelectionState {
		keys.forEach((key) => (state = state.clearBy(key)));
		return state;
	}

	public clearBy(key: FilterKey): FilterSelectionState {
		const partial = this.copy();
		partial.reactive.delete(key);
		return partial;
	}

	public removeSelection(payload: readonly IFilterPayload[], state: FilterSelectionState): FilterSelectionState {
		payload.forEach(({ key, value }) => (state = state.removeFor(key, value)));
		return state;
	}

	public removeFor(key: FilterKey, value: string): FilterSelectionState {
		const partial = this.copy();

		const record = partial.reactive.getFor(key);
		if (record?.length === 0) {
			return partial;
		}

		const newValue = partial.reactive.getFor(key).filter((v) => value !== v);
		if (newValue.length === 0) {
			partial.reactive.delete(key);
			return partial;
		}
		partial.reactive.set(key, newValue);

		return partial;
	}

	public mergeWith(other: FilterSelectionState): FilterSelectionState {
		const result = this.copy();
		result.reactive.mergeWith(other);

		return result;
	}

	public isEqual(other: FilterSelectionState): boolean {
		const selected = this.reactive.identifySelectedFilterKeys();
		const otherSelected = this.identifySelectedFiltersFor(other);

		if (selected.length !== otherSelected.length) {
			return false;
		}

		return selected.every((key) => this.isEqualOn(key, other));
	}

	private isEqualOn(key: FilterKey, other: FilterSelectionState): boolean {
		const values = this.reactive.getFor(key);
		const valuesOther = other.reactive.getFor(key);

		return values.length === valuesOther.length && values.every((v) => valuesOther.includes(v));
	}

	private copy(): FilterSelectionState {
		const result = new FilterSelectionState();
		result.reactive.replaceWith(this);

		return result;
	}
}
